import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, FilePlus, Receipt, Users, Tag, LineChart, CalendarClock, MessageSquare, Sliders, LogOut, Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/lib/api";
import QwLogo from "@/components/quickwash/QwLogo";

const items = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true, id: "nav-dashboard", tint: "bg-blue-50 text-[#2563EB]", activeTint: "bg-[#2563EB] text-white" },
  { to: "/admin/bills/new", icon: FilePlus, label: "New Bill", id: "nav-new-bill", tint: "bg-orange-50 text-[#F97316]", activeTint: "bg-[#F97316] text-white" },
  { to: "/admin/bills", icon: Receipt, label: "All Bills", id: "nav-bills", tint: "bg-emerald-50 text-emerald-600", activeTint: "bg-emerald-600 text-white" },
  { to: "/admin/customers", icon: Users, label: "Customers", id: "nav-customers", tint: "bg-violet-50 text-violet-600", activeTint: "bg-violet-600 text-white" },
  { to: "/admin/rates", icon: Tag, label: "Rate List", id: "nav-rates", tint: "bg-amber-50 text-amber-600", activeTint: "bg-amber-500 text-white" },
  { to: "/admin/reports", icon: LineChart, label: "Reports", id: "nav-reports", tint: "bg-rose-50 text-rose-600", activeTint: "bg-rose-500 text-white" },
  { to: "/admin/bookings", icon: CalendarClock, label: "Pickups", id: "nav-bookings", tint: "bg-cyan-50 text-cyan-600", activeTint: "bg-cyan-600 text-white" },
  { to: "/admin/messages", icon: MessageSquare, label: "Messages", id: "nav-messages", tint: "bg-teal-50 text-teal-600", activeTint: "bg-teal-600 text-white" },
  { to: "/admin/tag-editor", icon: Sliders, label: "Tag Editor", id: "nav-tag-editor", tint: "bg-fuchsia-50 text-fuchsia-600", activeTint: "bg-fuchsia-600 text-white" },
];

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-layout">
      <aside className="hidden md:flex flex-col w-64 bg-white border-r border-slate-200 sticky top-0 h-screen">
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-slate-100">
          <QwLogo size={38} />
          <div>
            <div className="font-extrabold text-slate-900" style={{ fontFamily: 'Poppins' }}>Quick Wash</div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-600">Admin Console</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={it.id}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <it.icon size={18} /> {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-100">
          <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-600 hover:bg-red-50 hover:text-red-700 transition-colors" data-testid="logout-btn">
            <LogOut size={18} /> Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="qw-glass-nav h-16 flex items-center justify-between px-4 md:px-8">
          <div>
            <div className="text-xs text-slate-500">Welcome back</div>
            <div className="font-semibold text-slate-900">{user?.name || "Admin"}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/admin/bills/new")} className="qw-btn-primary" data-testid="header-new-bill">+ New Bill</button>
            <div className="relative">
              <button className="qw-btn-ghost" aria-label="Notifications" data-testid="header-notifications"><Bell size={18}/></button>
            </div>
          </div>
        </header>
        <main className="p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
